"use client";

/**
 * Agenzax_E2E_멀티키_설계.md 6절: 페어링 시크릿(PSK). 서버는 이 값을 절대 저장하지 않는다 —
 * 이미 히스토리를 갖고 있는 키 보유자(주로 에이전트)가 로컬에만 갖고 있다가, 새 키 보유자가
 * 보낸 백필 요청의 서명을 자기 로컬 원문으로 직접 검증한다(서버는 블라인드 중계만 함).
 * WebCrypto HMAC-SHA256을 쓴다(브라우저·Node 둘 다 globalThis.crypto.subtle 지원).
 */

async function hmacKey(psk: string): Promise<CryptoKey> {
  const enc = new TextEncoder();
  return crypto.subtle.importKey("raw", enc.encode(psk), { name: "HMAC", hash: "SHA-256" }, false, ["sign", "verify"]);
}

export function generatePairingSecret(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(32));
  let binary = "";
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function signaturePayload(publicKeyBase64: string, timestamp: number): ArrayBuffer {
  const u8 = new TextEncoder().encode(`${publicKeyBase64}.${timestamp}`);
  return u8.buffer.slice(u8.byteOffset, u8.byteOffset + u8.byteLength) as ArrayBuffer;
}

export async function signBackfillRequest(psk: string, publicKeyBase64: string, timestamp: number): Promise<string> {
  const key = await hmacKey(psk);
  const sig = await crypto.subtle.sign("HMAC", key, signaturePayload(publicKeyBase64, timestamp));
  const bytes = new Uint8Array(sig);
  let binary = "";
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary);
}

export async function verifyBackfillRequest(psk: string, publicKeyBase64: string, timestamp: number, signatureBase64: string): Promise<boolean> {
  const key = await hmacKey(psk);
  const binary = atob(signatureBase64);
  const sigBytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) sigBytes[i] = binary.charCodeAt(i);
  return crypto.subtle.verify("HMAC", key, sigBytes, signaturePayload(publicKeyBase64, timestamp));
}
